import { fetchAccumulatedOfferById, fetchAccumulatedListingOfferById } from 'actions/OfferActions'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { useEffect } from 'react'
import { getAllOffers, getIsListingOfferEnabledForCurrentPage } from 'selectors/offerSelectors'
import { selectCancellationOptionBucket } from 'selectors/cancellationOptionsSelectors'

interface UseOfferOptions {
  disabled?: boolean
  requireSummaryOnly?: boolean
  preferListingOffer?: boolean
  bypassRegionFilter?: boolean
}

function useOffer<T extends App.OfferBase = App.AnyOffer>(
  offerId: string | undefined,
  options: UseOfferOptions = { },
): [T | undefined, boolean, any] {
  const dispatch = useAppDispatch()
  const listingPayloadEnabled = useAppSelector(getIsListingOfferEnabledForCurrentPage)
  const useListingEndpoint = listingPayloadEnabled && options.preferListingOffer && options.requireSummaryOnly && !options.bypassRegionFilter

  const fullOffer: any = useAppSelector(state => getAllOffers(state)[offerId!])
  const listingOffer = useAppSelector(state => useListingEndpoint ? state.offer.listingOffers[offerId!] : undefined)
  const offer = useListingEndpoint ? listingOffer ?? fullOffer : fullOffer

  const offerSummary = useAppSelector((state) =>
    state.offer.offerSummaries[offerId!] ||
    state.offer.bedbankOfferSummaries[offerId!],
  )

  const hasFullOffer = !!fullOffer && fullOffer !== offerSummary
  const alreadyFetchedWithBypass = useAppSelector(state => !!state.offer.offersBypassRegion?.[offerId!])
  const fetchedForAnotherBucket = useAppSelector(state =>
    !!state.offer.offers[offerId!] &&
    state.offer.offersCancellationOptionBucket?.[offerId!] !== selectCancellationOptionBucket(state),
  )

  const isFetchRequired = !!offerId && !options.disabled && (
    options.requireSummaryOnly
      ? !offer
      : (!hasFullOffer || fetchedForAnotherBucket || (!!options.bypassRegionFilter && !alreadyFetchedWithBypass))
  )
  useEffect(() => {
    if (isFetchRequired) {
      if (useListingEndpoint) {
        dispatch(fetchAccumulatedListingOfferById(offerId))
      } else {
        dispatch(fetchAccumulatedOfferById(offerId, undefined, undefined, options.bypassRegionFilter))
      }
    }
  }, [offerId, isFetchRequired, dispatch, options.bypassRegionFilter, useListingEndpoint])

  const fetching = useAppSelector(state => useListingEndpoint ? state.offer.listingOffersLoading[offerId!] : state.offer.offersLoading[offerId!]) && isFetchRequired
  const error = useAppSelector(state => useListingEndpoint ? state.offer.listingOfferErrors[offerId!] : state.offer.offerErrors[offerId!])

  return [
    offer,
    fetching,
    error,
  ]
}

export default useOffer
